/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { memo } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { Form } from 'semantic-ui-react';
import { Button, Checkbox, Text, Conditional } from '@cashfree-intl/coherent';
import { createStructuredSelector } from 'reselect';

import {
  captureBlur,
  clearSessionStorage,
  getItemFromLocalStorage,
  getReferrerUrl,
  generateReCAPTCHA,
  sentryCaptureException,
} from 'utils/common';

import Analytics from 'utils/analytics';
import EVENTS from 'constants/analytics';

import FormikComponent from 'components/Formik';
import FormInputWithDropdown from 'components/FormInputWithDropdown';
import CustomIcon from 'components/CustomIcons';

import {
  FRESHSALES_LEAD_SOURCE_ID,
  MOBILE_COUNTRY_CODE_OPTIONS,
  IMPORT_MOBILE_COUNTRY_CODE_OPTIONS,
} from '../../../utils/constants';

import {
  merchantDetailsSchema as validationSchema,
  merchantDetailsValidate as validate,
  importMerchantDetailsSchema as importsValidationSchema,
} from '../validation';
import {
  WHITELISTED_REFERRAL_URL_LIST,
  KEYWORDS_FOR_IMPORT_MERCHANT,
} from '../constants';

import makeSelectOnboardingContainer from '../../OnboardingContainer/selectors';

export function MerchantDetailsForm(props) {
  const {
    merchantDetails,
    referrer,
    enableLoader,
    onCreateAccount,
    isFormSubmitted,
    verifyAccountForImports,
    onboardingContainer,
    disableLoader,
  } = props;

  const { email, password } = onboardingContainer?.signupFirstStepData;

  const params = new URLSearchParams(window.location.search);

  let sourceAction = params.get('source-action') || '';

  console.log('Raw source-action value:', sourceAction);

  // Decode manually (in case it's still encoded)
  sourceAction = decodeURIComponent(sourceAction);

  // Check if all keywords exist in sourceAction
  const isImportMerchant = KEYWORDS_FOR_IMPORT_MERCHANT.every(keyword =>
    sourceAction.includes(keyword),
  );

  const submitMerchantDetailsForm = (values, actions) => {
    if (isImportMerchant) {
      enableLoader();

      const merchantData = {
        email,
        password,
        countryCode:
          values?.countryCode === 'CAN+1' || values?.countryCode === 'USA+1'
            ? '+1'
            : values?.countryCode,
        phone: values?.phone,
      };

      const onSubmitReCaptcha = captchaResponse => {
        verifyAccountForImports({ ...merchantData, captchaResponse });
      };

      generateReCAPTCHA({
        onSuccess: onSubmitReCaptcha,
        onError: () => {
          verifyAccountForImports({ merchantData, captchaResponse: ' ' });
          disableLoader();
          sentryCaptureException({
            errMsg: new Error(`Failed to load recaptcha`),
            priority: 'p0',
            label: 'auth',
          });
        },
      });
    } else {
      Analytics.trackEvent(
        EVENTS.SIGNUP.STEP_2.CREATE_ACCOUNT,
        {
          has_website: values.appType === 'website',
          url: values.merchantSiteUrl,
        },
        'OB',
      )();
      enableLoader();
      onCreateAccount({
        ...values,
        leadSourceId:
          FRESHSALES_LEAD_SOURCE_ID[referrer.toUpperCase()] ||
          FRESHSALES_LEAD_SOURCE_ID.DEFAULT,
      });
      actions.setSubmitting(false);
    }
  };

  const redirectToOnboarding = () => {
    window.dataLayer.push({
      event: 'signup',
      enhanced_conversion_data: {
        email: getItemFromLocalStorage('email'),
      },
    });
    // check if referral url is present
    const referralUrl = getReferrerUrl();

    if (
      referralUrl &&
      WHITELISTED_REFERRAL_URL_LIST.some(url => referralUrl.includes(url))
    ) {
      window.location.href = referralUrl;
      clearSessionStorage();
    } else {
      window.location.href = `${process.env.OB_BASE_PATH}`;
    }
  };

  const sendCountryCodeEvent = data => {
    if (data.name === 'countryCode')
      Analytics.trackEvent(
        EVENTS.SIGNUP.STEP_2.SELECT_COUNTRY_CODE,
        { countryCode: data.value },
        'OB',
      )();
  };

  return (
    <>
      {isFormSubmitted && redirectToOnboarding()}
      <FormikComponent
        initialValues={merchantDetails}
        validationSchema={
          isImportMerchant ? importsValidationSchema : validationSchema
        }
        validate={validate}
        onSubmit={submitMerchantDetailsForm}
      >
        {({
          values = {},
          handleFieldBlur,
          handleFieldChange,
          handleSubmit,
          errors,
          touched,
          setFieldValue,
          ...formikProps
        }) => {
          const disabledField =
            !formikProps.isValidating && formikProps.isSubmitting;

          return (
            <Form autoComplete="off" onSubmit={handleSubmit}>
              <div
                className={`phone-wrapper ${
                  isImportMerchant ? 'no-fixed-width' : ''
                }`}
              >
                <FormInputWithDropdown
                  label="Mobile Number"
                  inputFieldName="phone"
                  inputType="number"
                  inputFieldValue={values.phone}
                  dropdownFieldName="countryCode"
                  dropdownOptions={
                    isImportMerchant
                      ? IMPORT_MOBILE_COUNTRY_CODE_OPTIONS
                      : MOBILE_COUNTRY_CODE_OPTIONS
                  }
                  dropDownValue={values.countryCode}
                  dropdownPlaceholder="Select"
                  dropDownPosition="left"
                  dropdownIcon={<CustomIcon componentName="CHEVRONDOWN" />}
                  dropdownClassName="phone-dropdown"
                  errors={errors}
                  touched={touched}
                  handleFieldChange={(e, data) =>
                    handleFieldChange(e, data, sendCountryCodeEvent(data))
                  }
                  handleFieldBlur={(e, data) =>
                    captureBlur(
                      e,
                      data,
                      handleFieldBlur,
                      Analytics.trackEvent(
                        EVENTS.SIGNUP.STEP_2.PHONE_NUMBER,
                        null,
                        'OB',
                      ),
                    )
                  }
                  enableDropDownSearch
                  isLabelDropdown
                />
              </div>
              <Conditional if={!isImportMerchant}>
                <Checkbox
                  name="whatsappConsent"
                  label={
                    <label htmlFor="consent">
                      <Text variant="b12" color="bodyLight">
                        Receive account updates via WhatsApp
                      </Text>
                      <CustomIcon componentName="WHATSAPP_GREEN" />
                    </label>
                  }
                  className="custom-checkbox whatsapp-opt-in mr-btm-24"
                  checked={!!+values.whatsappConsent}
                  onChange={(e, data) =>
                    setFieldValue('whatsappConsent', `${+data.checked}`)
                  }
                />
              </Conditional>
              <Button
                fluid
                primary
                type="submit"
                disabled={disabledField}
                className="mr-top-16 mr-btm-40"
              >
                Create Account
              </Button>
            </Form>
          );
        }}
      </FormikComponent>
    </>
  );
}

const mapStateToProps = createStructuredSelector({
  onboardingContainer: makeSelectOnboardingContainer(),
});

MerchantDetailsForm.propTypes = {
  merchantDetails: PropTypes.object.isRequired,
  referrer: PropTypes.string.isRequired,
  enableLoader: PropTypes.func,
  onCreateAccount: PropTypes.func.isRequired,
  isFormSubmitted: PropTypes.bool.isRequired,
  verifyAccountForImports: PropTypes.func,
  onboardingContainer: PropTypes.object,
  disableLoader: PropTypes.func.isRequired,
};

const withConnect = connect(mapStateToProps);

export default compose(
  withConnect,
  memo,
)(MerchantDetailsForm);
